import _ from 'underscore';

const MaxAssetCountPerThemeToApply = {
  images: 5,
  headlines: 5,
  longHeadlines: 2,
  descriptions: 2,
};

/**
 * Gets a unique list of asset recommendation objects, including asset and recommendationId, from a list of themes,
 * without getting too many assets from each theme.
 * @param {Object[]} themes - List of theme objects with recommended text assets.
 * @param {string} field - Field name for text assets, e.g. 'headlines' or 'descriptions'.
 * @param {Number} [maxSize] - Maximum number of assets to get.
 * @returns {Object[]} - Array of asset objects, with asset and RecommendationId/suggestionId
 */
function getUniqueAssetMapFromThemes(themes, field, maxSize) {
  // do not get too many assets from each theme
  const maxSizePerTheme = MaxAssetCountPerThemeToApply[field];

  // use a Map to prevent duplicate assets
  const map = new Map();

  for (let i = 0; i < _.size(themes) && map.size < maxSize; i += 1) {
    // add assets from each theme
    const assets = _.get(themes, [i, field]);

    let assetsAddedFromTheme = 0;

    for (let j = 0; j < _.size(assets) && assetsAddedFromTheme < maxSizePerTheme && map.size < maxSize; j += 1) {
      let key;
      let value;

      if (field === 'images') {
        value = assets[j];
        key = _.get(value, 'suggestionId');
      } else {
        key = _.get(assets, [j, 'Text']);
        value = _.get(assets, [j, 'RecommendationId']);
      }

      // Prevent duplicate image suggestionId, prevent duplicate text
      if (!_.isEmpty(key) && !map.has(key)) {
        assetsAddedFromTheme += 1;
        map.set(key, value);
      }
    }
  }

  return map;
}

/**
 * Gets a unique list of asset recommendationIds from a list of themes, without getting too many assets from each theme.
 * @param {Object[]} themes - List of theme objects with recommended text assets.
 * @param {string} field - Field name for text assets, e.g. 'headlines' or 'descriptions'.
 * @param {Number} [maxSize] - Maximum number of assets to get.
 * @returns {string[]} - Array of text asset strings.
 */
export function getUniqueRecommendationIdsFromThemes(themes, field, maxSize = Number.MAX_SAFE_INTEGER) {
  const map = getUniqueAssetMapFromThemes(themes, field, maxSize);

  if (field === 'images') {
    return Array.from(map.keys());
  }

  return Array.from(map.values());
}

/**
 * Gets a unique list of asset recommendations from a list of themes, without getting too many assets from each theme.
 * @param {Object[]} themes - List of theme objects with recommended text assets.
 * @param {string} field - Field name for text assets, e.g. 'headlines' or 'descriptions'.
 * @param {Number} [maxSize] - Maximum number of assets to get.
 * @returns {string[]} - Array of text asset strings.
 */
export function getUniqueAssetsFromThemes(themes, field, maxSize = Number.MAX_SAFE_INTEGER) {
  const map = getUniqueAssetMapFromThemes(themes, field, maxSize);

  if (field === 'images') {
    return Array.from(map.values());
  }
  return Array.from(map.keys());
}
